import { useState } from "react";
import type { BlockReason, CreateBlockReason, UpdateBlockReason } from "@dentacore/shared-types";

import { RowMenu } from "../ui/popovers";
import styles from "./settings.module.css";

/**
 * Why a chair is held, when nobody is in it (STORY-046c, decisions G12 + G25).
 *
 * ── What this screen is for, in one sentence ───────────────────────────────
 *
 * A buffer block goes onto the chair's Google calendar, and **the label that
 * travels is a row from this list, never what somebody typed on the booking**.
 * A block has no patient, so D3's "initials and procedure" formatter has
 * neither to offer; taking the label from a clinical field verbatim would
 * eventually send *"Mrs Haddad — crown prep, allergic to latex"* to Google with
 * nobody realising it left the building.
 *
 * ── Why this screen exists at all ──────────────────────────────────────────
 *
 * The Phase 4 grill defaulted that chosen set to a fixed
 * `Maintenance / Cleaning / Admin / Training / Other` enum with no screen.
 * **Q27 reversed it on 2026-09-04**: a chosen set closes the leak whether it is
 * fixed or configured, so the enum bought nothing and cost *data is configured,
 * not coded*. A practice that wanted "Lab pickup" would have picked "Other",
 * and Google would have shown "Other" for ever.
 *
 * ── Retire, or delete, and the difference is real ──────────────────────────
 *
 * Retiring takes a reason out of the picker and leaves every block already held
 * under it reading correctly. Deleting removes it outright and **the server
 * refuses while anything uses it**, naming the count — so this offers both and
 * lets the refusal arrive rather than guessing which applies.
 */
export function BlockReasonsPanel({
  reasons,
  isAdmin,
  busy,
  onCreate,
  onUpdate,
  onRemove,
  onAcceptStarter,
}: {
  reasons: readonly BlockReason[] | null;
  /**
   * Presentation only, exactly as it is on the catalogue and inventory panels.
   *
   * **Defence in depth rather than the live guard**: Settings is admin-only
   * outright (S2, S3), so today nobody reaches this panel who could press the
   * buttons anyway — an attempt to write it out as a test proved that by
   * failing on `/settings` itself. It stays because the panel should not depend
   * on where it happens to be mounted, and the server refuses regardless.
   */
  isAdmin: boolean;
  busy: boolean;
  onCreate: (body: CreateBlockReason) => void;
  onUpdate: (reasonId: string, body: UpdateBlockReason, done: string) => void;
  onRemove: (reasonId: string, label: string) => void;
  onAcceptStarter: () => void;
}): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [labelInput, setLabelInput] = useState("");

  return (
    <section className={styles.card} data-testid="block-reasons-panel">
      <div className={styles.cardHead}>
        {/*
          The pane's lead already carries why this list exists and what leaves
          the building. This says the part that decides which button to press.
        */}
        <div>
          <h2 className={styles.cardTitle}>
            {reasons === null
              ? "Reasons"
              : `${String(reasons.length)} ${reasons.length === 1 ? "reason" : "reasons"}`}
          </h2>
        </div>
        {isAdmin && !adding && reasons !== null && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => setAdding(true)}
            data-testid="reason-add-open"
          >
            Add a reason
          </button>
        )}
      </div>

      {adding && (
        <form
          className={styles.cardBody}
          onSubmit={(event) => {
            event.preventDefault();
            if (newLabel.trim() === "") return;
            onCreate({ label: newLabel.trim() });
            setNewLabel("");
            setAdding(false);
          }}
        >
          <label className={styles.field}>
            <span className={styles.label}>Reason</span>
            <input
              className={styles.input}
              value={newLabel}
              onChange={(event) => setNewLabel(event.target.value)}
              maxLength={60}
              placeholder="Whatever you hold a chair for"
              autoFocus
              data-testid="new-reason-label"
            />
          </label>
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.primary}
              disabled={busy || newLabel.trim() === ""}
              data-testid="new-reason-add"
            >
              Add it
            </button>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => {
                setNewLabel("");
                setAdding(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {reasons === null ? (
        <div className={styles.cardBody}>
          <p className={styles.quiet}>Reading&hellip;</p>
        </div>
      ) : reasons.length === 0 ? (
        <div className={styles.cardBody}>
          <div className={styles.blank} data-testid="no-reasons">
            <p>
              No block reasons yet. Until there is one, a chair held for maintenance reaches Google
              with no label at all.
            </p>
            {isAdmin && (
              <button
                type="button"
                className={styles.ghost}
                disabled={busy}
                onClick={onAcceptStarter}
                data-testid="reason-starter"
              >
                Start from a suggested list
              </button>
            )}
          </div>
        </div>
      ) : (
        <ol className={styles.cardRows}>
          {reasons.map((reason) => (
            <li key={reason.id}>
              <div className={reason.isActive ? styles.row : styles.rowMuted}>
                {renamingId === reason.id ? (
                  <form
                    className={styles.inlineForm}
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (labelInput.trim() === "") return;
                      onUpdate(reason.id, { label: labelInput.trim() }, "Reason renamed.");
                      setRenamingId(null);
                    }}
                  >
                    <input
                      className={styles.input}
                      value={labelInput}
                      onChange={(event) => setLabelInput(event.target.value)}
                      maxLength={60}
                      aria-label="Block reason"
                      autoFocus
                      data-testid="reason-label-input"
                    />
                    <button
                      type="submit"
                      className={styles.primary}
                      disabled={busy || labelInput.trim() === ""}
                      data-testid="reason-rename-save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.ghost}
                      disabled={busy}
                      onClick={() => setRenamingId(null)}
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <span className={styles.rowBody}>
                      <span className={styles.rowTitle}>
                        <span className={styles.rowName} data-testid="reason-label">
                          {reason.label}
                        </span>
                        {!reason.isActive && (
                          <span className={styles.tagQuiet} data-testid="reason-retired">
                            Retired
                          </span>
                        )}
                      </span>
                    </span>

                    {isAdmin && (
                      /*
                        **One ⋯ per row** (decision CS10) — Edit, Retire or
                        Bring back, and Delete, all still here. Delete sits
                        apart in the danger colour: it is the only one of the
                        three that destroys anything.

                        Delete is **offered even when it will be refused**:
                        whether a reason is in use is a fact about the whole
                        calendar, and asking the server is the only way to know
                        it. The refusal names the count and says to retire
                        instead.
                      */
                      <span className={styles.rowActions}>
                        <RowMenu
                          label={`More for ${reason.label}`}
                          testId="reason-menu"
                          items={[
                            {
                              // Edit, not Rename — Anthony QA #12, as on chairs.
                              label: "Edit",
                              testId: "reason-rename",
                              disabled: busy,
                              onSelect: () => {
                                setRenamingId(reason.id);
                                setLabelInput(reason.label);
                              },
                            },
                            {
                              label: reason.isActive ? "Retire" : "Bring back",
                              testId: "reason-retire",
                              disabled: busy,
                              onSelect: () => {
                                onUpdate(
                                  reason.id,
                                  { isActive: !reason.isActive },
                                  reason.isActive ? "Reason retired." : "Reason back in the list.",
                                );
                              },
                            },
                            {
                              label: "Delete",
                              testId: "reason-delete",
                              danger: true,
                              disabled: busy,
                              onSelect: () => {
                                onRemove(reason.id, reason.label);
                              },
                            },
                          ]}
                        />
                      </span>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
